import { environment } from 'src/environments/environment';
import {
  isCubeConnectorDialer,
  isCubeDialerAllowed,
  isCubeIframeVisible,
  shouldMountCubeConnectorBridge,
} from './cube/cube-dialer-mode';

export { setCubeIframeVisible, isCubeDialerAllowed } from './cube/cube-dialer-mode';
import { isOzonetelIframeDialer, isOzonetelSdkDialer } from './ozonetel/ozonetel-dialer-mode';

export type CallingDialerType = 'cube' | 'ozonetel' | 'centro' | 'progressive' | 'none';

const DIALER_OVERRIDE_KEY = 'moneytor_dialer_override';
const OZONETEL_ENABLED_KEY = 'moneytor_ozonetel_dialer_enabled';

export function getDialerOverride(): CallingDialerType | null {
  const value = localStorage.getItem(DIALER_OVERRIDE_KEY);
  return value === 'cube' || value === 'ozonetel' || value === 'centro' ? value : null;
}

export function setDialerOverride(type: 'cube' | 'ozonetel' | 'centro' | null): void {
  if (type === 'cube' || type === 'ozonetel' || type === 'centro') {
    localStorage.setItem(DIALER_OVERRIDE_KEY, type);
  } else {
    localStorage.removeItem(DIALER_OVERRIDE_KEY);
  }
}

export function getActiveDialerFromEnv(): 'cube' | 'ozonetel' | null {
  if (isCubeDialerAllowed()) {
    return 'cube';
  }
  return null;
}

export function isCentroDialerActive(callingType?: string | null): boolean {
  return callingType === 'centro';
}

export function isOzonetelDialerEnabled(): boolean {
  const ls = localStorage.getItem(OZONETEL_ENABLED_KEY);
  if (ls === 'false') {
    return false;
  }
  if (ls === 'true') {
    return true;
  }
  const envFlag = (environment as { ozonetel_dialer_enabled?: boolean }).ozonetel_dialer_enabled;
  return envFlag === true;
}

export function setOzonetelDialerEnabled(enabled: boolean | null): void {
  if (enabled === null) {
    localStorage.removeItem(OZONETEL_ENABLED_KEY);
    return;
  }
  localStorage.setItem(OZONETEL_ENABLED_KEY, enabled ? 'true' : 'false');
}

export function resolveCallingDialerType(
  callingService?: string | null,
  remarks?: Record<string, any> | null
): CallingDialerType {
  const override = getDialerOverride();
  if (override === 'ozonetel' && isOzonetelDialerEnabled()) {
    return 'ozonetel';
  }
  if (override === 'cube' && isCubeDialerAllowed()) {
    return 'cube';
  }
  if (override === 'centro') {
    return 'centro';
  }

  const normalized = (callingService || '').toLowerCase().trim();
  if (normalized === 'centro' || normalized.includes('centro')) {
    return 'centro';
  }
  if (isOzonetelDialerEnabled()) {
    if (normalized === 'ozonetel' || normalized.includes('ozonetel')) {
      return 'ozonetel';
    }
    if (remarks?.['ozonetelDetails']?.agentId) {
      return 'ozonetel';
    }
  }
  if (isCubeDialerAllowed()) {
    if (normalized === 'cubesoft' || normalized === 'cube' || remarks?.['cubeDetails']?.AgentId) {
      return 'cube';
    }
  }
  if (remarks?.['centroDetails']?.agentId) {
    return 'centro';
  }
  return 'none';
}

export function toWidgetCallingType(type: CallingDialerType): string | undefined {
  if (type === 'none') {
    return undefined;
  }
  return type;
}

export function shouldShowCallingWidget(callingType?: string | null): boolean {
  if (callingType === 'cube') {
    return isCubeDialerAllowed();
  }
  if (callingType === 'progressive') {
    return true;
  }
  if (callingType === 'ozonetel') {
    return isOzonetelDialerEnabled() && isOzonetelSdkDialer();
  }
  if (callingType === 'centro') {
    return true;
  }
  return false;
}

export function shouldShowOzonetelIframe(callingType?: string | null): boolean {
  return isOzonetelDialerEnabled() && callingType === 'ozonetel' && isOzonetelIframeDialer();
}

export function shouldShowCubeIframe(callingType?: string | null): boolean {
  return callingType === 'cube' && isCubeDialerAllowed() && shouldMountCubeConnectorBridge();
}

export function isCubeIframeUiVisible(): boolean {
  return isCubeIframeVisible();
}

export function isCubeDialerActive(callingType?: string | null): boolean {
  return callingType === 'cube' && isCubeDialerAllowed();
}

export function isOzonetelDialerActive(callingType?: string | null): boolean {
  return isOzonetelDialerEnabled() && callingType === 'ozonetel';
}

export function hasCubeBrowserSdk(): boolean {
  return isCubeConnectorDialer();
}
